import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, Clock, TrendingUp, Lock, Sparkles } from 'lucide-react';
import { QuizPlayer } from '../components/QuizPlayer';
import { useProgress } from '../context/ProgressContext';
import { quizzes, getQuizzesForAge } from '../data/quizzesData';
export function QuizzesPage() {
  const navigate = useNavigate();
  const [selectedQuiz, setSelectedQuiz] = useState<string | null>(null);
  const {
    progress
  } = useProgress();
  const userAge = progress.preferences.age || 16;
  const availableQuizzes = getQuizzesForAge(userAge);
  const lockedQuizzes = quizzes.filter(quiz => userAge < quiz.ageRange[0] || userAge > quiz.ageRange[1]);
  const getCategoryColor = (category: string) => {
    const colors = {
      consent: 'from-purple-500 to-pink-500',
      relationships: 'from-rose-500 to-red-500',
      safety: 'from-orange-500 to-amber-500',
      health: 'from-teal-500 to-cyan-500',
      emotions: 'from-blue-500 to-indigo-500',
      online: 'from-green-500 to-emerald-500'
    };
    return colors[category as keyof typeof colors] || 'from-gray-500 to-slate-500';
  };
  const getDifficultyBadge = (difficulty: string) => {
    const badges = {
      easy: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        label: 'Beginner'
      },
      medium: {
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        label: 'Intermediate'
      },
      hard: {
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        label: 'Advanced'
      }
    };
    return badges[difficulty as keyof typeof badges] || badges.medium;
  };
  if (selectedQuiz) {
    return <QuizPlayer quizId={selectedQuiz} onExit={() => setSelectedQuiz(null)} data-id="element-912" />;
  }
  return <div className="min-h-screen w-full bg-gradient-to-br from-green-50 to-blue-50 dark:from-slate-900 dark:via-green-950 dark:to-blue-950 pb-20 md:pb-8" data-id="element-913">
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-green-100 dark:border-slate-700 sticky top-0 z-10" data-id="element-914">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-915">
          <button onClick={() => navigate('/home')} className="flex items-center space-x-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300" data-id="element-916">
            <ArrowLeft className="w-5 h-5" data-id="element-917" />
            <span className="font-medium" data-id="element-918">Back</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-919">
            Fun Quizzes
          </h1>
          <div className="w-20" data-id="element-920" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-921">
        <div className="text-center mb-8" data-id="element-922">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 flex items-center justify-center space-x-2" data-id="element-923">
            <Sparkles className="w-8 h-8 text-yellow-500" data-id="element-924" />
            <span data-id="element-925">Test Your Knowledge! 🎯</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-926">
            Challenge yourself with fun, educational quizzes. Learn while you
            play!
          </p>
        </div>
        {/* Available Quizzes */}
        <div className="mb-12" data-id="element-927">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2" data-id="element-928">
            <Brain className="w-6 h-6 text-green-500" data-id="element-929" />
            <span data-id="element-930">Quizzes For You (Age {userAge})</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-931">
            {availableQuizzes.map(quiz => {
            const isCompleted = progress.completedQuizzes.includes(quiz.id);
            const difficultyBadge = getDifficultyBadge(quiz.difficulty);
            return <button key={quiz.id} onClick={() => setSelectedQuiz(quiz.id)} className="group bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-left transform hover:-translate-y-2 border-2 border-transparent hover:border-green-200 dark:hover:border-green-700 relative overflow-hidden" data-id="element-932">
                  {isCompleted && <div className="absolute top-4 right-4 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg" data-id="element-933">
                      <span className="text-white text-xl" data-id="element-934">✓</span>
                    </div>}
                  <div className={`w-16 h-16 bg-gradient-to-br ${getCategoryColor(quiz.category)} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`} data-id="element-935">
                    <Brain className="w-9 h-9 text-white" data-id="element-936" />
                  </div>
                  <div className="flex items-center space-x-2 mb-3" data-id="element-937">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${difficultyBadge.color}`} data-id="element-938">
                      {difficultyBadge.label}
                    </span>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-939">
                      <Clock className="w-3 h-3 mr-1" data-id="element-940" />
                      <span data-id="element-941">{quiz.estimatedTime} min</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors" data-id="element-942">
                    {quiz.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm" data-id="element-943">
                    {quiz.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-944">
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-945">
                      {quiz.questions.length} questions
                    </span>
                    <div className="flex items-center text-sm font-semibold text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" data-id="element-946">
                      {isCompleted ? 'Play Again' : 'Start Quiz'}{' '}
                      <span className="ml-2 group-hover:translate-x-2 transition-transform" data-id="element-947">
                        →
                      </span>
                    </div>
                  </div>
                </button>;
          })}
          </div>
        </div>
        {/* Locked Quizzes */}
        {lockedQuizzes.length > 0 && <div data-id="element-948">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2" data-id="element-949">
              <Lock className="w-6 h-6 text-gray-400" data-id="element-950" />
              <span data-id="element-951">Coming Soon (Other Age Groups)</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-952">
              {lockedQuizzes.slice(0, 3).map(quiz => <div key={quiz.id} className="bg-gray-100 dark:bg-slate-800/50 rounded-2xl p-6 opacity-60 relative" data-id="element-953">
                  <div className="absolute top-4 right-4" data-id="element-954">
                    <Lock className="w-6 h-6 text-gray-400" data-id="element-955" />
                  </div>
                  <div className="w-16 h-16 bg-gray-300 dark:bg-slate-700 rounded-2xl flex items-center justify-center mb-4" data-id="element-956">
                    <Brain className="w-9 h-9 text-gray-500" data-id="element-957" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-700 dark:text-gray-400 mb-2" data-id="element-958">
                    {quiz.title}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mb-4" data-id="element-959">
                    {quiz.description}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium" data-id="element-960">
                    Available for ages {quiz.ageRange[0]}-{quiz.ageRange[1]}
                  </p>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}